import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const UserManagementTable = ({ users, onUserAction, totalCount = 0, currentPage = 1, pageSize = 15, onPageChange, onFilterChange }) => {
  const USERS_PER_PAGE = pageSize;
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('none');
  const [pageInput, setPageInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', username: '', email: '', role: 'user', avatarUrl: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [subModal, setSubModal] = useState({ open: false, user: null });
  const [subForm, setSubForm] = useState({ subscriptionTier: 'free', subStatus: 'active', startDate: '', endDate: '', billingAmount: '' });
  const [subErrors, setSubErrors] = useState({});
  const [isSavingSub, setIsSavingSub] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState({ open: false, user: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ step: 0, user: null }); // step 0=closed, 1=first, 2=second
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState({ step: 0, ids: [] }); // bulk delete 2-step
  const [bulkActionMenuOpen, setBulkActionMenuOpen] = useState(false);
  const [stripeWarning, setStripeWarning] = useState({ open: false, callback: null });
  const [bulkStatusConfirm, setBulkStatusConfirm] = useState({ open: false, ids: [], action: null }); // bulk deactivate/activate
  const [creditModal, setCreditModal] = useState({ open: false, user: null });
  const [creditForm, setCreditForm] = useState({ amount: '', type: 'add' });
  const [isSavingCredit, setIsSavingCredit] = useState(false);
  const actionMenuRef = useRef(null);

  // ปิด action menu เมื่อคลิกนอก
  useEffect(() => {
    if (!actionMenuOpen) return;
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuOpen]);

  // ปิด bulk action menu เมื่อคลิกนอก
  const bulkMenuRef = useRef(null);
  useEffect(() => {
    if (!bulkActionMenuOpen) return;
    const handleClickOutside = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkActionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bulkActionMenuOpen]);

  const statusOptions = [
    { value: 'all',   label: 'All Users' },
    { value: 'free',  label: 'Free' },
    { value: 'solo',  label: 'Solo' },
    { value: 'pro',   label: 'Pro' },
    { value: 'team',  label: 'Team' },
  ];

  const handleSort = (field) => {
    let newField = field;
    let newDir = 'asc';
    if (sortField === field) {
      if (sortDirection === 'asc') {
        newDir = 'desc';
      } else {
        newField = null;
        newDir = 'none';
      }
    }
    setSortField(newField);
    setSortDirection(newDir);
    onFilterChange?.(searchQuery, filterStatus, newField, newDir);
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedUsers(users?.filter(u => u?.role !== 'admin')?.map(u => u?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success',
      inactive: 'bg-error/10 text-error'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles?.[status]}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getSubscriptionBadge = (subscription) => {
    const styles = {
      free:          'text-muted-foreground',
      solo:          'text-orange-300',
      pro:           'text-accent',
      team:          'text-primary',
      team_member:   'text-yellow-300',
      enterprise:    'text-purple-400',
    };

    const labels = {
      free:         'Free',
      solo:         'Solo',
      pro:          'Pro',
      team:         'Team',
      team_member:  'Team Member',
      enterprise:   'Enterprise',
    };

    const key = subscription?.toLowerCase() || 'free';
    const style = styles[key] || styles.free;
    const label = labels[key] || subscription?.charAt(0)?.toUpperCase() + subscription?.slice(1) || 'Free';

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style}`}>
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const openEditModal = (user) => {
    setEditForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      username: user?.username || '',
      email: user?.email || '',
      role: user?.role || 'user',
      avatarUrl: user?.avatarUrl || '',
    });
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditErrors({});
    setEditModal({ open: true, user });
  };

  const openSubModal = (user) => {
    setSubForm({
      subscriptionTier: user?.subscription || 'free',
      subStatus: user?.subscriptionStatus || 'active',
      startDate: user?.subscriptionStartDate ? user.subscriptionStartDate.slice(0, 10) : '',
      endDate: user?.subscriptionEndDate ? user.subscriptionEndDate.slice(0, 10) : '',
      billingAmount: user?.billingAmount ?? '',
    });
    setSubErrors({});
    setSubModal({ open: true, user });
  };

  const executeSubSave = async () => {
    setIsSavingSub(true);
    try {
      await onUserAction('subscriptionSave', subModal.user.id, {
        subscription_tier: subForm.subscriptionTier,
        sub_status: subForm.subStatus,
        start_date: subForm.startDate || null,
        end_date: subForm.endDate || null,
        billing_amount: subForm.billingAmount !== '' ? Number(subForm.billingAmount) : null,
      });
      setSubModal({ open: false, user: null });
    } catch (err) {
      console.error('Subscription save error:', err);
    } finally {
      setIsSavingSub(false);
    }
  };

  const handleSubSubmit = async () => {
    if (!subModal.user) return;
    const errors = {};
    if (!subForm.startDate) errors.startDate = 'Start date is required';
    if (Object.keys(errors).length > 0) {
      setSubErrors(errors);
      return;
    }
    setSubErrors({});

    // Check if user has an active Stripe subscription — warn admin before any modification
    try {
      const { data: sub } = await supabase
        .from('user_subscriptions')
        .select('stripe_subscription_id')
        .eq('user_id', subModal.user.id)
        .in('status', ['active', 'past_due'])
        .maybeSingle();

      if (sub?.stripe_subscription_id) {
        setStripeWarning({ open: true, callback: executeSubSave });
        return;
      }
    } catch (err) {
      console.error('Stripe check error:', err);
    }

    await executeSubSave();
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (editForm.username?.trim() && !/^[a-zA-Z0-9._]+$/.test(editForm.username.trim())) {
      newErrors.username = 'Only a-z, 0-9, . and _ are allowed';
    }
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!editModal.user) return;
    if (!validateEditForm()) return;
    const cleanUsername = editForm.username?.trim().toLowerCase() || '';
    setEditForm(prev => ({ ...prev, username: cleanUsername }));
    setIsUploading(true);
    try {
      await onUserAction('editSave', editModal.user.id, {
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        username: cleanUsername,
        role: editForm.role,
        avatar_url: editForm.avatarUrl,
        avatar_file: avatarFile || null,
      });
      setEditModal({ open: false, user: null });
      setAvatarFile(null);
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
        setAvatarPreview(null);
      }
    } catch (err) {
      console.error('Edit submit error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreditSubmit = async () => {
    const amount = parseInt(creditForm.amount, 10);
    if (!creditModal.user || isNaN(amount) || amount <= 0) return;
    setIsSavingCredit(true);
    try {
      await onUserAction('adjustCredits', creditModal.user.id, {
        amount,
        type: creditForm.type,
      });
      setCreditModal({ open: false, user: null });
      setCreditForm({ amount: '', type: 'add' });
    } catch (err) {
      console.error('Credit adjust error:', err);
    } finally {
      setIsSavingCredit(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search by name, username or email..."
              value={searchQuery}
              onChange={(e) => {
                const val = e?.target?.value;
                setSearchQuery(val);
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = setTimeout(() => {
                  onFilterChange?.(val, filterStatus, sortField, sortDirection);
                }, 400);
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={(val) => {
                setFilterStatus(val);
                onFilterChange?.(searchQuery, val, sortField, sortDirection);
              }}
              className="w-40"
            />
            {selectedUsers?.length > 0 && (
              <div className="relative" ref={bulkMenuRef}>
                <Button
                  variant="outline"
                  iconName="ChevronDown"
                  iconPosition="right"
                  onClick={() => setBulkActionMenuOpen(prev => !prev)}
                >
                  Actions ({selectedUsers?.length})
                </Button>
                {bulkActionMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-floating z-20">
                    <button
                      onClick={() => {
                        setBulkStatusConfirm({ open: true, ids: selectedUsers, action: 'deactivate' });
                        setBulkActionMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                    >
                      <Icon name="UserX" size={16} />
                      Deactivate Selected
                    </button>
                    <button
                      onClick={() => {
                        setBulkStatusConfirm({ open: true, ids: selectedUsers, action: 'activate' });
                        setBulkActionMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                    >
                      <Icon name="UserCheck" size={16} />
                      Activate Selected
                    </button>
                    <div className="border-t border-border my-1" />
                    <button
                      onClick={() => {
                        setBulkDeleteConfirm({ step: 1, ids: selectedUsers });
                        setBulkActionMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-smooth flex items-center gap-2"
                    >
                      <Icon name="Trash2" size={16} />
                      Delete Selected
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto orange-scrollbar">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={
                    users?.filter(u => u?.role !== 'admin')?.length > 0 &&
                    selectedUsers?.length === users?.filter(u => u?.role !== 'admin')?.length
                  }
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  User
                  <Icon name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('subscription')}
              >
                <div className="flex items-center gap-2">
                  Subscription
                  <Icon name={sortField === 'subscription' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('subscriptionStartDate')}
              >
                <div className="flex items-center gap-2">
                  Sub Period
                  <Icon name={sortField === 'subscriptionStartDate' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-2">
                  Status
                  <Icon name={sortField === 'status' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('joinDate')}
              >
                <div className="flex items-center gap-2">
                  Join Date
                  <Icon name={sortField === 'joinDate' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('lastActivity')}
              >
                <div className="flex items-center gap-2">
                  Last Activity
                  <Icon name={sortField === 'lastActivity' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('credits')}
              >
                <div className="flex items-center gap-2">
                  Credits
                  <Icon name={sortField === 'credits' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="flex-shrink-0" />
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => handleSelectUser(user?.id)}
                    disabled={user?.role === 'admin'}
                    className="w-4 h-4 rounded border-border disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover bg-muted"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{user?.name}</span>
                        {user?.role === 'admin' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary uppercase tracking-wide">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{user?.email}</div>
                      {user?.username && (
                        <div className="text-xs text-muted-foreground/60">@{user?.username}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getSubscriptionBadge(user?.subscription)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {user?.subscriptionStartDate || user?.subscriptionEndDate ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-muted-foreground">
                        Start: {user?.subscriptionStartDate
                          ? formatDate(user.subscriptionStartDate)
                          : '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        End: {user?.subscriptionEndDate
                          ? formatDate(user.subscriptionEndDate)
                          : 'No expiry'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {formatDate(user?.joinDate)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {formatDate(user?.lastActivity)}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-foreground">
                  {(user?.credits ?? 0).toLocaleString()}
                </td>

                <td className="px-6 py-4 text-right">
                  <div
                    className="relative inline-block"
                    ref={actionMenuOpen === user?.id ? actionMenuRef : null}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="MoreVertical"
                      onClick={() => setActionMenuOpen(actionMenuOpen === user?.id ? null : user?.id)}
                    />
                    {actionMenuOpen === user?.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-floating z-10">
                        <button
                          onClick={() => {
                            openEditModal(user);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="Edit" size={16} />
                          Edit User
                        </button>
                        <button
                          onClick={() => {
                            openSubModal(user);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="Crown" size={16} />
                          Modify Subscription
                        </button>
                        <button
                          onClick={() => {
                            setCreditModal({ open: true, user });
                            setCreditForm({ amount: '', type: 'add' });
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name="Coins" size={16} />
                          Adjust Credits
                        </button>
                        <button
                          onClick={() => {
                            setToggleConfirm({ open: true, user });
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                        >
                          <Icon name={user?.status === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                          {user?.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        {user?.role !== 'admin' && (
                          <>
                            <div className="border-t border-border my-1"></div>
                            <button
                              onClick={() => {
                                setDeleteConfirm({ step: 1, user });
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-smooth flex items-center gap-2"
                            >
                              <Icon name="Trash2" size={16} />
                              Delete User
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No users found matching your criteria</p>
        </div>
      )}

      {/* Pagination */}
      {totalCount > USERS_PER_PAGE && (() => {
        const totalPages = Math.ceil(totalCount / USERS_PER_PAGE);
        const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
        const endPage = Math.min(totalPages, startPage + 4);
        const pages = [];
        for (let i = startPage; i <= endPage; i++) pages.push(i);

        return (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <span className="text-sm text-muted-foreground">
              {(currentPage - 1) * USERS_PER_PAGE + 1}–{Math.min(currentPage * USERS_PER_PAGE, totalCount)} of {totalCount}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              {pages.map((p) => (
                <button
                  key={p}
                  onClick={() => onPageChange?.(p)}
                  className={`min-w-[36px] py-1.5 rounded-lg text-sm font-medium transition-smooth ${currentPage === p ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-muted text-foreground'}`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const num = parseInt(pageInput, 10);
                  if (num >= 1 && num <= totalPages) onPageChange?.(num);
                  setPageInput('');
                }}
                className="flex items-center gap-1.5 ml-3"
              >
                <input
                  type="text"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
                  placeholder="Go to"
                  className="w-16 px-2 py-1.5 rounded-lg text-sm border border-border bg-background text-foreground text-center focus:outline-none focus:border-primary"
                />
                <button
                  type="submit"
                  className="px-2.5 py-1.5 rounded-lg text-sm font-medium border border-border hover:bg-muted transition-smooth"
                >
                  Go
                </button>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Adjust Credits Modal */}
      {creditModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setCreditModal({ open: false, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Adjust Credits</h3>
              <button
                onClick={() => setCreditModal({ open: false, user: null })}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* User info */}
            <div className="flex items-center gap-3 mb-6 p-3 bg-muted/50 rounded-lg">
              {creditModal.user?.avatarUrl ? (
                <img src={creditModal.user.avatarUrl} alt={creditModal.user.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                  {creditModal.user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
              )}
              <div>
                <div className="font-medium text-foreground text-sm">{creditModal.user?.name}</div>
                <div className="text-xs text-muted-foreground">
                  Current credits: <span className="font-semibold text-foreground">{(creditModal.user?.credits ?? 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Add / Subtract toggle */}
            <div className="flex rounded-lg overflow-hidden border border-border mb-4">
              <button
                onClick={() => setCreditForm(prev => ({ ...prev, type: 'add' }))}
                className={`flex-1 py-2 text-sm font-medium transition-smooth flex items-center justify-center gap-1.5 ${
                  creditForm.type === 'add'
                    ? 'bg-success/10 text-success'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Plus" size={15} />
                Add
              </button>
              <button
                onClick={() => setCreditForm(prev => ({ ...prev, type: 'subtract' }))}
                className={`flex-1 py-2 text-sm font-medium transition-smooth flex items-center justify-center gap-1.5 border-l border-border ${
                  creditForm.type === 'subtract'
                    ? 'bg-error/10 text-error'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Minus" size={15} />
                Subtract
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Amount</label>
              <Input
                type="number"
                min="1"
                value={creditForm.amount}
                onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>

            {/* Preview */}
            {creditForm.amount && !isNaN(parseInt(creditForm.amount, 10)) && parseInt(creditForm.amount, 10) > 0 && (
              <div className="mt-3 px-3 py-2 bg-muted/50 rounded-md text-sm text-muted-foreground">
                After adjustment:{' '}
                <span className="font-semibold text-foreground">
                  {Math.max(0, (creditModal.user?.credits ?? 0) + (creditForm.type === 'add' ? 1 : -1) * parseInt(creditForm.amount, 10)).toLocaleString()}
                </span>{' '}credits
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setCreditModal({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreditSubmit}
                disabled={isSavingCredit || !creditForm.amount || parseInt(creditForm.amount, 10) <= 0}
              >
                {isSavingCredit ? 'Saving...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Subscription Modal */}
      {subModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSubModal({ open: false, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Modify Subscription</h3>
              <button
                onClick={() => setSubModal({ open: false, user: null })}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* User info header */}
            <div className="flex items-center gap-3 mb-6 p-3 bg-muted/50 rounded-lg">
              {subModal.user?.avatarUrl ? (
                <img src={subModal.user.avatarUrl} alt={subModal.user.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                  {subModal.user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
              )}
              <div>
                <div className="font-medium text-foreground text-sm">{subModal.user?.name}</div>
                <div className="text-xs text-muted-foreground">{subModal.user?.email}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Subscription Tier</label>
                {subModal.user?.subscription === 'team_member' ? (
                  <Input value="Team Member" disabled={true} />
                ) : (
                  <Select
                    options={[
                      { value: 'free', label: 'Free' },
                      { value: 'solo', label: 'Solo' },
                      { value: 'pro', label: 'Pro' },
                      { value: 'team', label: 'Team' },
                    ]}
                    value={subForm.subscriptionTier}
                    onChange={(val) => setSubForm(prev => ({ ...prev, subscriptionTier: val }))}
                  />
                )}
                {subModal.user?.subscription === 'team_member' && (
                  <p className="text-xs text-muted-foreground mt-1">Team member's tier is managed by the team owner.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Subscription Status</label>
                <Select
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'expired', label: 'Expired' },
                  ]}
                  value={subForm.subStatus}
                  onChange={(val) => setSubForm(prev => ({ ...prev, subStatus: val }))}
                />
                {/* Cancelled info label — แสดงเฉพาะเมื่อ status ใน DB เป็น cancelled */}
                {subModal.user?.subscriptionStatus === 'cancelled' && subModal.user?.cancelledAt && (
                  <div className="mt-2 px-3 py-2 bg-error/10 rounded-md text-sm text-error">
                    Cancelled at {new Date(subModal.user.cancelledAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {subModal.user?.cancelledByName && (
                      <>, by <span className="font-medium">{subModal.user.cancelledByName}</span></>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Billing Amount
                  <span className="ml-2 text-xs text-muted-foreground font-normal">(read-only)</span>
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={subForm.billingAmount}
                  disabled={true}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
                <Input
                  type="date"
                  value={subForm.startDate}
                  onChange={(e) => {
                    setSubForm(prev => ({ ...prev, startDate: e.target.value }));
                    if (e.target.value) setSubErrors(prev => ({ ...prev, startDate: undefined }));
                  }}
                  error={subErrors.startDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  End Date
                  <span className="ml-2 text-xs text-muted-foreground font-normal">(read-only)</span>
                </label>
                <Input
                  type="date"
                  value={subForm.endDate}
                  disabled={true}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setSubModal({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button onClick={handleSubSubmit} disabled={isSavingSub}>
                {isSavingSub ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setEditModal({ open: false, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Edit User</h3>
              <button
                onClick={() => setEditModal({ open: false, user: null })}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Avatar Preview + File Upload */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative flex-shrink-0">
                  {avatarPreview || editForm.avatarUrl ? (
                    <img
                      src={avatarPreview || editForm.avatarUrl}
                      alt="Avatar"
                      className="w-20 h-20 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xl border-2 border-border">
                      {editForm.firstName?.[0] || ''}{editForm.lastName?.[0] || ''}
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
                    <Icon name="Camera" size={20} className="text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="flex flex-col items-center gap-1">
                  {avatarFile && (
                    <>
                      <span className="text-sm text-muted-foreground truncate max-w-[200px]">{avatarFile.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarFile(null);
                          if (avatarPreview) {
                            URL.revokeObjectURL(avatarPreview);
                            setAvatarPreview(null);
                          }
                        }}
                        className="text-xs text-error hover:underline"
                      >
                        Remove selected file
                      </button>
                    </>
                  )}
                  {(editForm.avatarUrl || avatarPreview) && !avatarFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditForm(prev => ({ ...prev, avatarUrl: '' }));
                        setAvatarFile(null);
                        if (avatarPreview) {
                          URL.revokeObjectURL(avatarPreview);
                          setAvatarPreview(null);
                        }
                      }}
                      className="text-xs text-error hover:underline"
                    >
                      Remove avatar
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">First Name</label>
                <Input
                  value={editForm.firstName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value.replace(/\s/g, '') }))}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Last Name</label>
                <Input
                  value={editForm.lastName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value.replace(/\s/g, '') }))}
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                <Input
                  value={editForm.username}
                  onChange={(e) => {
                    setEditForm(prev => ({ ...prev, username: e.target.value.replace(/\s/g, '') }));
                    if (editErrors.username) setEditErrors(prev => ({ ...prev, username: '' }));
                  }}
                  placeholder="a-z, 0-9, . and _ only"
                  error={editErrors.username}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <Input
                  type="text"
                  value={editForm.email}
                  disabled={true}
                  placeholder="Email"
                />
                <p className="text-xs text-muted-foreground mt-1">Email address cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                <Select
                  options={[
                    { value: 'user', label: 'User' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                  value={editForm.role}
                  onChange={(val) => setEditForm(prev => ({ ...prev, role: val }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditModal({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button onClick={handleEditSubmit} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Deactivate / Activate Confirm Modal */}
      {bulkStatusConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setBulkStatusConfirm({ open: false, ids: [], action: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${bulkStatusConfirm.action === 'deactivate' ? 'bg-warning/10' : 'bg-success/10'}`}>
                <Icon
                  name={bulkStatusConfirm.action === 'deactivate' ? 'UserX' : 'UserCheck'}
                  size={20}
                  className={bulkStatusConfirm.action === 'deactivate' ? 'text-warning' : 'text-success'}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {bulkStatusConfirm.action === 'deactivate' ? 'Deactivate' : 'Activate'} {bulkStatusConfirm.ids.length} Users?
                </h3>
                <p className="text-sm text-muted-foreground">
                  {bulkStatusConfirm.ids.length} user{bulkStatusConfirm.ids.length > 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {bulkStatusConfirm.action === 'deactivate'
                ? `These ${bulkStatusConfirm.ids.length} user${bulkStatusConfirm.ids.length > 1 ? 's' : ''} will lose access to the platform immediately. You can reactivate them at any time.`
                : `These ${bulkStatusConfirm.ids.length} user${bulkStatusConfirm.ids.length > 1 ? 's' : ''} will regain full access to the platform based on their subscription plan.`}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBulkStatusConfirm({ open: false, ids: [], action: null })}
              >
                Cancel
              </Button>
              <Button
                variant={bulkStatusConfirm.action === 'deactivate' ? 'destructive' : 'default'}
                onClick={() => {
                  onUserAction(
                    bulkStatusConfirm.action === 'deactivate' ? 'bulkDeactivate' : 'bulkActivate',
                    bulkStatusConfirm.ids
                  );
                  setBulkStatusConfirm({ open: false, ids: [], action: null });
                  setSelectedUsers([]);
                }}
              >
                {bulkStatusConfirm.action === 'deactivate' ? 'Yes, Deactivate' : 'Yes, Activate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirm Modal — Step 1 */}
      {bulkDeleteConfirm.step === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setBulkDeleteConfirm({ step: 0, ids: [] })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  Delete {bulkDeleteConfirm.ids.length} Users?
                </h3>
                <p className="text-sm text-muted-foreground">
                  {bulkDeleteConfirm.ids.length} user{bulkDeleteConfirm.ids.length > 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              You're about to permanently delete {bulkDeleteConfirm.ids.length} user account{bulkDeleteConfirm.ids.length > 1 ? 's' : ''}. All associated data — including projects, settings, and subscription history — will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBulkDeleteConfirm({ step: 0, ids: [] })}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => setBulkDeleteConfirm(prev => ({ ...prev, step: 2 }))}
              >
                Delete Users
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirm Modal — Step 2 (Final) */}
      {bulkDeleteConfirm.step === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setBulkDeleteConfirm({ step: 0, ids: [] })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Are you absolutely sure?</h3>
                <p className="text-sm text-muted-foreground">
                  {bulkDeleteConfirm.ids.length} user{bulkDeleteConfirm.ids.length > 1 ? 's' : ''} will be deleted
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              This action <span className="font-semibold text-error">cannot be undone</span>. All {bulkDeleteConfirm.ids.length} selected user{bulkDeleteConfirm.ids.length > 1 ? 's' : ''} and their data will be gone forever.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBulkDeleteConfirm({ step: 0, ids: [] })}
              >
                No, Keep Users
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onUserAction('bulkDelete', bulkDeleteConfirm.ids);
                  setBulkDeleteConfirm({ step: 0, ids: [] });
                  setSelectedUsers([]);
                  onPageChange?.(1);
                }}
              >
                Yes, Delete Forever
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate / Activate Confirm Modal */}
      {toggleConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setToggleConfirm({ open: false, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${toggleConfirm.user?.status === 'active' ? 'bg-warning/10' : 'bg-success/10'}`}>
                <Icon
                  name={toggleConfirm.user?.status === 'active' ? 'UserX' : 'UserCheck'}
                  size={20}
                  className={toggleConfirm.user?.status === 'active' ? 'text-warning' : 'text-success'}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {toggleConfirm.user?.status === 'active' ? 'Deactivate User?' : 'Activate User?'}
                </h3>
                <p className="text-sm text-muted-foreground">{toggleConfirm.user?.name}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {toggleConfirm.user?.status === 'active'
                ? 'This user will lose access to the platform immediately. You can reactivate them at any time.'
                : 'This user will regain full access to the platform based on their subscription plan.'}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setToggleConfirm({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button
                variant={toggleConfirm.user?.status === 'active' ? 'destructive' : 'default'}
                onClick={() => {
                  onUserAction('toggleStatus', toggleConfirm.user?.id);
                  setToggleConfirm({ open: false, user: null });
                }}
              >
                {toggleConfirm.user?.status === 'active' ? 'Yes, Deactivate' : 'Yes, Activate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal — Step 1 */}
      {deleteConfirm.step === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm({ step: 0, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete User?</h3>
                <p className="text-sm text-muted-foreground">{deleteConfirm.user?.name}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              You're about to permanently delete this user account. All associated data — including projects, settings, and subscription history — will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ step: 0, user: null })}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteConfirm(prev => ({ ...prev, step: 2 }))}
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal — Step 2 (Final) */}
      {deleteConfirm.step === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm({ step: 0, user: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Are you absolutely sure?</h3>
                <p className="text-sm text-muted-foreground">{deleteConfirm.user?.name}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              This action <span className="font-semibold text-error">cannot be undone</span>. Once deleted, this user and all their data will be gone forever.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ step: 0, user: null })}
              >
                No, Keep User
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onUserAction('delete', deleteConfirm.user?.id);
                  setDeleteConfirm({ step: 0, user: null });
                }}
              >
                Yes, Delete Forever
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Stripe Payment Warning Modal */}
      {stripeWarning.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setStripeWarning({ open: false, callback: null })}
          />
          <div className="relative bg-card border border-border rounded-lg shadow-floating w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Active Stripe Subscription Detected</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              This user has an active payment subscription managed by Stripe.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Changing the subscription tier here will only update the database. It will <strong className="text-foreground">not</strong> modify the user's Stripe subscription, billing cycle, or payment amount. This may cause a mismatch between the database and Stripe.
            </p>
            <div className="p-3 bg-muted/50 rounded-lg mb-5 text-xs text-muted-foreground">
              <strong className="text-foreground">Recommendation:</strong> Use the Stripe Dashboard to manage plan changes for paying users, so that billing and credits stay in sync automatically.
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setStripeWarning({ open: false, callback: null })}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  setStripeWarning({ open: false, callback: null });
                  if (stripeWarning.callback) await stripeWarning.callback();
                }}
              >
                Proceed Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTable;